import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { sendPushNotification, logNotification } from '../sendPushNotification';
import theme from '../theme';

function validaCodiceFiscale(cf) {
  return /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/.test(cf.toUpperCase());
}

function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: '',
    codiceFiscale: '',
    indirizzo: '',
    cap: '',
    citta: '',
    provincia: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const showError = (msg) => {
    setMessage(msg);
    setMessageType('error');
  };

  const showSuccess = (msg) => {
    setMessage(msg);
    setMessageType('success');
  };

  const handleSubmit = async () => {
    if (!form.fullName.trim()) { showError('Inserisci il tuo nome completo.'); return; }
    if (!form.email.trim()) { showError('Inserisci la tua email.'); return; }
    if (!form.password || form.password.length < 6) { showError('La password deve essere di almeno 6 caratteri.'); return; }
    if (!form.codiceFiscale.trim()) { showError('Inserisci il tuo codice fiscale.'); return; }
    if (!validaCodiceFiscale(form.codiceFiscale)) { showError('Il codice fiscale non è valido. Controlla e riprova.'); return; }
    if (!form.indirizzo.trim()) { showError('Inserisci il tuo indirizzo di residenza.'); return; }
    if (!form.cap.trim()) { showError('Inserisci il CAP.'); return; }
    if (!form.citta.trim()) { showError('Inserisci la città di residenza.'); return; }
    if (!form.provincia.trim()) { showError('Inserisci la provincia.'); return; }

    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email: form.email.trim(),
      password: form.password,
      options: {
        data: { full_name: form.fullName.trim() }
      }
    });

    if (error) { showError(error.message); setLoading(false); return; }

    if (data.user) {
      const indirizzoCompleto = `${form.indirizzo.trim()}, ${form.cap.trim()} ${form.citta.trim()} (${form.provincia.trim().toUpperCase()})`;
      await supabase.from('profiles').upsert([{
        id: data.user.id,
        full_name: form.fullName.trim(),
        city: form.citta.trim(),
        codice_fiscale: form.codiceFiscale.trim().toUpperCase(),
        indirizzo_residenza: indirizzoCompleto,
      }]);

      // Promemoria: verifica la tua identità prima di poter lavorare
      sendPushNotification({
        userId: data.user.id,
        title: '🛡️ Verifica la tua identità',
        body: 'Prima di candidarti o pubblicare annunci, verifica la tua identità dal tuo profilo.',
        url: '/profilo',
      });
      logNotification({
        userId: data.user.id,
        type: 'identity_reminder',
        title: '🛡️ Verifica la tua identità',
        body: 'Ricordati di verificare la tua identità prima di candidarti o pubblicare annunci.',
        url: '/profilo',
      });
    }

    setLoading(false);
    showSuccess('Registrazione effettuata! Controlla la tua email per confermare l\'account.');
  };

  return (
    <div style={{ maxWidth: theme.layout.maxWidth, margin: '0 auto', fontFamily: theme.font.family }}>
      <div style={{ background: theme.colors.primary, padding: '40px 20px', color: theme.colors.white, textAlign: 'center' }}>
        <h1 style={{ margin: 0, fontSize: theme.font.size.hero }}>Jobbetty</h1>
        <p style={{ margin: '8px 0 0', opacity: 0.85, fontSize: theme.font.size.lg }}>Trova lavoretti vicino a te</p>
      </div>

      <div style={{ padding: '30px 20px 60px' }}>
        <h2 style={{ marginBottom: 6, color: theme.colors.textPrimary }}>Crea account</h2>
        <p style={{ fontSize: theme.font.size.base, color: theme.colors.textMuted, marginBottom: theme.spacing.xxl }}>Tutti i campi sono obbligatori</p>

        {/* Dati personali */}
        <div style={{ fontSize: theme.font.size.sm, fontWeight: 700, color: theme.colors.primary, marginBottom: theme.spacing.md, textTransform: 'uppercase', letterSpacing: 1 }}>
          Dati personali
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>Nome completo</label>
          <input name="fullName" value={form.fullName} onChange={handleChange}
            placeholder="Es. Mario Rossi"
            style={{ width: '100%', padding: '12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.lg, boxSizing: 'border-box' }} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>Codice fiscale</label>
          <input name="codiceFiscale" value={form.codiceFiscale} onChange={handleChange}
            placeholder="Es. RSSMRA85M01H501Z"
            maxLength={16}
            style={{ width: '100%', padding: '12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.lg, boxSizing: 'border-box', textTransform: 'uppercase', letterSpacing: 1 }} />
          <div style={{ fontSize: theme.font.size.xs, color: '#bbb', marginTop: 4 }}>16 caratteri — lo trovi sul retro della carta d'identità o sul tesserino sanitario</div>
        </div>

        {/* Indirizzo di residenza */}
        <div style={{ fontSize: theme.font.size.sm, fontWeight: 700, color: theme.colors.primary, margin: '20px 0 12px', textTransform: 'uppercase', letterSpacing: 1 }}>
          Indirizzo di residenza
        </div>
        <div style={{ fontSize: theme.font.size.xs, color: theme.colors.textMuted, marginBottom: theme.spacing.md }}>
          Richiesto per adempimenti fiscali (Direttiva DAC7)
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>Via e numero civico</label>
          <input name="indirizzo" value={form.indirizzo} onChange={handleChange}
            placeholder="Es. Via Roma 12"
            style={{ width: '100%', padding: '12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.lg, boxSizing: 'border-box' }} />
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <div style={{ flex: '0 0 100px' }}>
            <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>CAP</label>
            <input name="cap" value={form.cap} onChange={handleChange}
              placeholder="43100" maxLength={5} type="number"
              style={{ width: '100%', padding: '12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.lg, boxSizing: 'border-box' }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>Città</label>
            <input name="citta" value={form.citta} onChange={handleChange}
              placeholder="Es. Parma"
              style={{ width: '100%', padding: '12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.lg, boxSizing: 'border-box' }} />
          </div>
          <div style={{ flex: '0 0 70px' }}>
            <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>Prov.</label>
            <input name="provincia" value={form.provincia} onChange={handleChange}
              placeholder="PR" maxLength={2}
              style={{ width: '100%', padding: '12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.lg, boxSizing: 'border-box', textTransform: 'uppercase' }} />
          </div>
        </div>

        {/* Credenziali */}
        <div style={{ fontSize: theme.font.size.sm, fontWeight: 700, color: theme.colors.primary, margin: '20px 0 12px', textTransform: 'uppercase', letterSpacing: 1 }}>
          Credenziali di accesso
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>Email</label>
          <input name="email" type="email" value={form.email} onChange={handleChange}
            placeholder="la@tua.email"
            style={{ width: '100%', padding: '12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.lg, boxSizing: 'border-box' }} />
        </div>

        <div style={{ marginBottom: theme.spacing.xxl }}>
          <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>Password</label>
          <input name="password" type="password" value={form.password} onChange={handleChange}
            placeholder="Minimo 6 caratteri"
            style={{ width: '100%', padding: '12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.lg, boxSizing: 'border-box' }} />
        </div>

        {/* Note privacy */}
        <div style={{ background: theme.colors.background, borderRadius: theme.radius.md, padding: 12, marginBottom: theme.spacing.lg, fontSize: theme.font.size.sm, color: '#888', lineHeight: 1.6 }}>
          🔒 I tuoi dati fiscali sono protetti e trattati in conformità al GDPR. Vengono raccolti esclusivamente per adempimenti fiscali previsti dalla Direttiva DAC7 (UE 2021/514) e non vengono condivisi con terzi salvo obblighi di legge.
        </div>

        {message && (
          <div style={{
            background: messageType === 'error' ? theme.colors.errorBg : theme.colors.primaryLight,
            color: messageType === 'error' ? theme.colors.errorDark : theme.colors.primary,
            padding: 12, borderRadius: theme.radius.sm, marginBottom: theme.spacing.lg, fontSize: theme.font.size.md
          }}>
            {message}
          </div>
        )}

        <button onClick={handleSubmit} disabled={loading} style={{
          width: '100%', background: loading ? theme.colors.textMuted : theme.colors.primary, color: theme.colors.white,
          border: 'none', borderRadius: theme.radius.lg, padding: 14,
          fontSize: theme.font.size.xl, cursor: loading ? 'not-allowed' : 'pointer', marginBottom: theme.spacing.lg
        }}>
          {loading ? 'Registrazione in corso...' : 'Registrati'}
        </button>

        <p style={{ textAlign: 'center', color: theme.colors.textSecondary, fontSize: theme.font.size.md }}>
          Hai già un account?{' '}
          <span onClick={() => navigate('/login')} style={{ color: theme.colors.primary, cursor: 'pointer', fontWeight: 600 }}>
            Accedi
          </span>
        </p>
      </div>
    </div>
  );
}

export default Register;